import { useMemo, useState } from 'react';
import { episodes } from '@/mocks/episodes';
import EpisodeCard from '@/components/feature/EpisodeCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import usePageMeta from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

type SortKey = 'number' | 'date';

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: 'number', label: '按剧集顺序', icon: 'ri-sort-asc' },
  { key: 'date', label: '按更新日期', icon: 'ri-time-line' },
];

export default function Episodes() {
  usePageMeta({
    title: '剧集列表 - 女友的妈妈',
    description:
      '《女友的妈妈》全集剧集列表，可按剧集顺序或更新日期浏览每一集的剧情简介、首播时间与相关演员信息。',
    keywords: '女友的妈妈剧集列表,全集分集剧情,女友的妈妈分集介绍',
  });

  const [sort, setSort] = useState<SortKey>('number');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const list = [...episodes];
    if (sort === 'number') {
      list.sort((a, b) => a.number - b.number);
    } else {
      list.sort((a, b) => b.airDate.localeCompare(a.airDate));
    }
    return list;
  }, [sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    setSort(key);
    setPage(1);
  };

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '剧集列表' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4">
            剧集列表
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">
            《女友的妈妈》全集共 12 集，已全部完结。点击任意剧集卡片，查看该集的剧情简介、演职人员与相关推荐。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleSort(option.key)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    sort === option.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  <i className={option.icon}></i>
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs md:text-sm text-foreground-500">
              共 <span className="text-foreground-900 font-medium">{sorted.length}</span> 集 · 第{' '}
              <span className="text-foreground-900 font-medium">{current}</span> / {totalPages} 页
            </p>
          </div>

          {pageItems.length === 0 ? (
            <EmptyState
              icon="ri-film-line"
              title="暂无剧集内容，敬请期待"
              description="当前筛选条件下没有找到剧集，试试重置筛选后再看看。"
              actionLabel="重置筛选"
              onAction={() => {
                setSort('number');
                setPage(1);
              }}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {pageItems.map((episode) => (
                  <EpisodeCard key={episode.id} episode={episode} />
                ))}
              </div>
              <Pagination current={current} total={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </>
  );
}